import React from 'react';

/**
 * KPI card (design `.card.kpi`): mono label with an optional chip, a big mono value, an
 * optional progress track and a caption. Pass `children` instead of `value` for a custom
 * body (e.g. a value beside a ring, or a sparkline). A named group so each card has an
 * accessible label for tests and screen readers.
 */
export default function KpiCard({ label, value, unit, size = 'md', sub, chip, chipTone = 'danger', tone = 'default', track, accentBorder = false, children }: {
  label: string;
  value?: React.ReactNode;
  /** Unit after the value (`%`, `/ 100`): 18px on `lg` (Main), 15px muted on `md` (Admin). */
  unit?: React.ReactNode;
  /** `lg` = Main dashboard `.kv` (38px, track 14px below); `md` = Admin `.kv.sm` (32px, track 10px below). */
  size?: 'md' | 'lg';
  sub?: React.ReactNode;
  chip?: React.ReactNode;
  chipTone?: 'danger' | 'warning';
  tone?: 'default' | 'success' | 'danger';
  track?: React.ReactNode;
  /** Accent-line border (the Exam list "Due this week" card). */
  accentBorder?: boolean;
  children?: React.ReactNode;
}) {
  const VALUE = { default: 'text-ink', success: 'text-success', danger: 'text-danger-ink' }[tone];
  const CHIP = chipTone === 'warning' ? 'bg-warning-bg text-warning-ink' : 'bg-danger-bg text-danger-ink';
  return (
    <section role="group" aria-label={label} className={`flex min-w-0 flex-col gap-1.5 rounded-[20px] border bg-surface p-[22px] ${accentBorder ? 'border-accent-line' : 'border-line'} shadow-card`}>
      <div className="flex items-center justify-between gap-2">
        <span className="min-w-0 font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">{label}</span>
        {chip && (typeof chip === 'string'
          ? <span className={`flex-none rounded-full px-2 py-[3px] font-mono text-xs font-semibold ${CHIP}`}>{chip}</span>
          : <span className="flex-none">{chip}</span>)}
      </div>
      {children ?? (
        <div className={`mt-2 whitespace-nowrap font-mono ${size === 'lg' ? 'text-[38px]' : 'text-[32px]'} font-semibold leading-none tracking-[-0.04em] ${VALUE}`}>
          {value}
          {unit != null && <span className={size === 'lg' ? 'text-[18px]' : 'text-[15px] text-muted'}>{unit}</span>}
        </div>
      )}
      {track && <div className={size === 'lg' ? 'mt-3.5' : 'mt-2.5'}>{track}</div>}
      {sub && <div className="text-xs text-muted">{sub}</div>}
    </section>
  );
}
